import React, { useEffect, useMemo, useState } from "react";
import { Button, Typography } from "antd";
import axios from "axios";
import LiquidGlass from "./LiquidGlass";
import History from "./History";

const { Title } = Typography;

const AdminUserHistory = ({ username, axiosInstance, isMobile, onBack }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fallback axios with token + refresh similar to the rest of the app
  const client = useMemo(() => {
    if (axiosInstance) return axiosInstance;
    const instance = axios.create({
      baseURL: "/api",
      headers: { "Content-Type": "application/json" },
    });
    instance.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem("token");
        if (token) config.headers["Authorization"] = `Bearer ${token}`;
        return config;
      },
      (err) => Promise.reject(err)
    );
    instance.interceptors.response.use(
      (response) => response,
      async (err) => {
        const originalRequest = err.config || {};
        if (err?.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;
          try {
            const refreshToken = localStorage.getItem("refresh_token");
            const refreshResponse = await axios.post("api/refresh_tokens/", {
              refresh_token: refreshToken,
            });
            if (refreshResponse.status === 200) {
              const { access_token: newToken, refresh_token: newRefreshToken } =
                refreshResponse.data;
              localStorage.setItem("token", newToken);
              localStorage.setItem("refresh_token", newRefreshToken);
              originalRequest.headers = originalRequest.headers || {};
              originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
              return instance(originalRequest);
            }
          } catch {
            localStorage.removeItem("token");
            localStorage.removeItem("refresh_token");
          }
        }
        return Promise.reject(err);
      }
    );
    return instance;
  }, [axiosInstance]);

  // Simple retry wrapper with exponential backoff for transient errors
  async function requestWithRetry(requestFn, maxRetries = 2, initialDelayMs = 300) {
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        return await requestFn();
      } catch (err) {
        const status = err?.response?.status;
        const isNetworkError = !err?.response;
        const isServerError = status >= 500 && status < 600;
        const isTimeout = err?.code === "ECONNABORTED";
        const retriable = isNetworkError || isServerError || isTimeout;
        if (!retriable || attempt === maxRetries) throw err;
        const delay = initialDelayMs * Math.pow(2, attempt);
        await new Promise((r) => setTimeout(r, delay));
      }
    }
  }

  const refresh = async () => {
    if (!username) return;
    setLoading(true);
    setError(null);
    try {
      const resp = await client.post("/admin/users/history/", {
        username,
      });
      const data = resp?.data || {};
      setHistory(Array.isArray(data.history) ? data.history : []);
    } catch (e) {
      const status = e?.response?.status;
      const detail =
        e?.response?.data?.detail ||
        e?.message ||
        (status ? `HTTP ${status}` : "Failed to load history");
      setError(detail);
      setHistory([]);
    } finally {
      setLoading(false);
    }
  };

  const toJsonStoragePathFromTcxLink = (tcxUrl) => {
    try {
      if (typeof tcxUrl !== "string") return null;
      const prefix = "https://storage.googleapis.com/";
      let rest = tcxUrl.startsWith(prefix) ? tcxUrl.slice(prefix.length) : tcxUrl;
      // Drop query string
      rest = rest.split("?")[0];
      // Replace extension .tcx with .json (case insensitive)
      const lower = rest.toLowerCase();
      const idx = lower.lastIndexOf(".tcx");
      if (idx < 0) return null;
      const pathNoExt = rest.slice(0, idx);
      const jsonPath = pathNoExt + ".json";
      // Return bucket/blob
      return jsonPath;
    } catch {
      return null;
    }
  };

  const handleRequestJsonUrl = async (tcxUrl) => {
    try {
      const bucketAndBlob = toJsonStoragePathFromTcxLink(tcxUrl);
      if (!bucketAndBlob || bucketAndBlob.indexOf("/") <= 0) {
        throw new Error("Invalid TCX link");
      }
      const resp = await requestWithRetry(() =>
        client.post("/sign_storage_url/", {
          storage_link: bucketAndBlob, // "bucket/blob.json"
        })
      );
      const signed = resp?.data?.signed_url;
      if (signed) {
        window.open(signed, "_blank", "noopener,noreferrer");
      }
    } catch (e) {
      const status = e?.response?.status;
      const detail =
        e?.response?.data?.detail ||
        e?.message ||
        (status ? `HTTP ${status}` : "Failed to sign JSON url");
      setError(detail);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [username]);

  return (
    <div>
      <LiquidGlass className="liquid-history" style={{ marginBottom: 12 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <Title level={4} style={{ margin: 0 }}>
            Admin • {username} • History
          </Title>
          <div style={{ display: "flex", gap: 8 }}>
            <Button onClick={refresh} loading={!!loading}>
              Refresh
            </Button>
            {onBack ? (
              <Button onClick={onBack} type="primary" className="primary-glass-btn">
                Back to Admin
              </Button>
            ) : null}
          </div>
        </div>
        {error ? (
          <div style={{ color: "#b00020", marginTop: 8 }}>Error: {error}</div>
        ) : null}
      </LiquidGlass>
      <History
        history={history}
        isMobile={!!isMobile}
        onRefresh={refresh}
        refreshing={!!loading}
        withJsonColumn
        onRequestJsonUrl={handleRequestJsonUrl}
      />
    </div>
  );
};

export default AdminUserHistory;


